import { cloneDeep, get, isEmpty, set, uniqBy } from 'lodash'
import * as yup from 'yup'
import { isRequirementRef as isGroupRequirementRef } from '../../../../util/groupRequirement.util'
import { isRequirementRef as isLocationRequirementRef } from '../../../../util/namedLocationRequirement.util'
import { isRequirementRef as isUserRequirementRef } from '../../../../util/userRequirement.util'
import {
  isRequirementRef as isAuthenticationContextRequirementRef,
  toRequirementRef as toAuthenticationContextRequirementRef,
} from '../../../../util/authenticationContextRequirement.util'
import { isRequirementRef as isAuthenticationStrengthRequirementRef } from '../../../../util/authenticationStrengthRequirement.util'
import {
  ALL_SERVICE_PRINCIPALS_IN_TENANT,
  isRequirementRef as isServicePrincipalRequirementRef,
} from '../../../../util/servicePrincipalRequirement.util'
import {
  isRequirementRef as isCustomAuthenticationFactorRequirementRef,
  toRequirementRef as toCustomAuthenticationFactorRequirementRef,
} from '../../../../util/customAuthenticationFactorRequirement.util'
import { isGuidFormat } from '../../../../util/misc.util'
import { isAnyRequirementRef } from '../../../../util/requirementRef.util'
import {
  CONDITIONS_INPUTS_LIST,
  CONTINUOUS_ACCESS_EVALUATION_MODES,
  GENERAL_INPUTS_LIST,
  GRANT_CONTROLS_INPUTS_LIST,
  INPUT_KEYS,
  INPUT_LIST,
  INPUT_POLICY_PATH,
  INPUT_TO_EMPTY_VALUE,
  SESSION_CONTROLS_INPUTS_LIST,
  isApplicationInput,
} from '../policy_feature/inputs'
import {
  BASE_APPLICATIONS,
  BASE_INCLUDE_APPLICATIONS,
  BASE_INCLUDE_LOCATIONS,
  BASE_INCLUDE_USERS,
  BASE_LOCATIONS,
  BASE_USERS,
  DEFAULT_EMPTY_POLICY,
  KNOWN_MICROSOFT_APPLICATIONS,
} from './constants'
import {
  FORM_KEYS_SKIP_OPTION_VALIDATION,
  TENANT_SPECIFIC_ID_INPUTS,
  collectUniquePolicyPathValues,
  hasDisplayableFormValue,
  pruneEmptyMappedPolicyObjects,
} from './nestedPolicyForm'

export { hasDisplayableFormValue }

const POLICY_UPDATE_STATUS = {
  PENDING: 'PENDING',
}

const BUILT_IN_CONTROLS = {
  APPROVED_APPLICATION: 'approvedApplication',
  BLOCK: 'block',
  COMPLIANT_APPLICATION: 'compliantApplication',
  MFA: 'mfa',
  PASSWORD_CHANGE: 'passwordChange',
}

const PLATFORM_VALUES = {
  ALL: 'all',
  ANDROID: 'android',
  IOS: 'iOS',
  LINUX: 'linux',
  MAC_OS: 'macOS',
  WINDOWS: 'windows',
  WINDOWS_PHONE: 'windowsPhone',
}

const ALL_DEVICE_PLATFORMS = [
  PLATFORM_VALUES.ANDROID,
  PLATFORM_VALUES.IOS,
  PLATFORM_VALUES.WINDOWS,
  PLATFORM_VALUES.WINDOWS_PHONE,
  PLATFORM_VALUES.MAC_OS,
  PLATFORM_VALUES.LINUX,
]

const APP_PROTECTION_SUPPORTED_PLATFORMS = new Set([
  PLATFORM_VALUES.ANDROID,
  PLATFORM_VALUES.IOS,
  PLATFORM_VALUES.WINDOWS,
])

const APPROVED_CLIENT_APP_SUPPORTED_PLATFORMS = new Set([PLATFORM_VALUES.ANDROID, PLATFORM_VALUES.IOS])

const GRANT_CONTROL_OPERATORS = {
  AND: 'AND',
}

const INCLUDE_APPLICATIONS_ALL = 'All'
const INCLUDE_APPLICATIONS_NONE = 'None'

const NAMED_LOCATION_STATUS = {
  DELETING: 'DELETING',
}

const NAMED_LOCATION_UPDATE_ACTION = {
  DELETE: 'DELETE',
}

const BUILT_IN_AUTHENTICATION_STRENGTHS = [
  {
    authStrengthId: '00000000-0000-0000-0000-000000000002',
    displayName: 'Multifactor authentication',
  },
  {
    authStrengthId: '00000000-0000-0000-0000-000000000003',
    displayName: 'Passwordless MFA',
  },
  {
    authStrengthId: '00000000-0000-0000-0000-000000000004',
    displayName: 'Phishing resistant MFA',
  },
]

const LEGACY_GUEST_OR_EXTERNAL_USER_TYPE_BITMASK_VALUES = [
  'internalGuest',
  'b2bCollaborationGuest',
  'b2bCollaborationMember',
  'b2bDirectConnectUser',
  'otherExternalUser',
  'serviceProvider',
]

const GUEST_OR_EXTERNAL_USER_POLICY_KEYS = [
  INPUT_KEYS.includeGuestsOrExternalUsers,
  INPUT_KEYS.excludeGuestsOrExternalUsers,
]

const EXTERNAL_TENANTS_MEMBERSHIP_KIND = {
  ALL: 'all',
  ENUMERATED: 'enumerated',
}

const LEGACY_MULTI_VALUE_ENUM_BITMASK_VALUES = {
  [INPUT_KEYS.transferMethods]: ['deviceCodeFlow', 'authenticationTransfer'],
  [INPUT_KEYS.includeGuestsOrExternalUsers]: LEGACY_GUEST_OR_EXTERNAL_USER_TYPE_BITMASK_VALUES,
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: LEGACY_GUEST_OR_EXTERNAL_USER_TYPE_BITMASK_VALUES,
}

// Hide locations that are in the process of being deleted from location dropdowns
const shouldHideNamedLocation = (location = {}) =>
  location?.deleted === true ||
  location?.isDeleted === true ||
  location?.status === NAMED_LOCATION_STATUS.DELETING ||
  location?.updateAction === NAMED_LOCATION_UPDATE_ACTION.DELETE

// Check if the form has implicit included locations
export const hasImplicitIncludedLocations = (form) =>
  form?.excludeLocations?.length === 1 &&
  form?.excludeLocations?.includes('AllTrusted') &&
  !form?.includeLocations?.length

// A template whose excludeLocations resolves to a value (a global constant or an attached named
// location requirement) can only ever deploy successfully once includeLocations is addressed too
// (Graph rejects a "locations" condition that has excludeLocations without includeLocations) — see
// hasExcludeLocationsMissingInclude for the equivalent deploy-time re-check. includeLocations
// counts as addressed if it already carries a value, or if it's left as a prompted field for the
// deploying technician to fill in; only a completely unaddressed includeLocations — no value and
// no prompt — makes the template impossible to ever deploy, so authoring it is blocked here.
export const hasUnaddressedExcludeOnlyLocations = (policyBody, promptedInputs = []) => {
  const excludeLocations = get(policyBody, INPUT_POLICY_PATH[INPUT_KEYS.excludeLocations]) || []
  const includeLocations = get(policyBody, INPUT_POLICY_PATH[INPUT_KEYS.includeLocations]) || []

  if (!excludeLocations.length || includeLocations.length > 0) {
    return false
  }
  if (hasImplicitIncludedLocations({ excludeLocations, includeLocations })) {
    return false
  }

  return !promptedInputs.includes(INPUT_KEYS.includeLocations)
}

export const isPasswordChangeSelected = (form) => form?.builtInControls?.includes(BUILT_IN_CONTROLS.PASSWORD_CHANGE)

export const hasPasswordChangeAllApplications = (form) => form?.includeApplications?.includes(INCLUDE_APPLICATIONS_ALL)

export const hasPasswordChangeUserRisk = (form) => Boolean(form?.userRiskLevels?.length)

export const getPolicyStatusOptions = (intl) => [
  {
    label: intl.formatMessage({ id: 'GENERAL.ENABLED' }),
    value: 'enabled',
  },
  {
    label: intl.formatMessage({ id: 'GENERAL.DISABLED' }),
    value: 'disabled',
  },
  {
    label: intl.formatMessage({ id: 'POLICY_TEMPLATES.REPORT_ONLY' }),
    value: 'enabledForReportingButNotEnforced',
  },
]

export const getPolicyStatusMessage = ({ intl, value }) => {
  const options = getPolicyStatusOptions(intl)
  return options.find((option) => option.value === value)?.label
}

export const isPolicySyncing = (policy) => policy?.updateStatus === POLICY_UPDATE_STATUS.PENDING

export const isPolicyEmpty = (rawPolicy) => isEmpty(rawPolicy?.conditions)

/**
 * Checks if an auth strength id is global (non tenant specific)
 * @param {string} authStrengthId
 * @returns true if it's a non tenant specific auth strength, false otherwise
 */
export const isGlobalAuthStrength = (authStrengthId) => authStrengthId?.includes('00000000-0000-0000-0000')

// Stored on template meta_data so create/detail/deploy can name a custom strength that
// filterTenantProperties nulls out of policyBody (tenant-specific, cannot be carried).
export const UNSUPPORTED_AUTHENTICATION_STRENGTH_META_KEY = 'unsupportedAuthenticationStrength'

/**
 * Returns the custom (tenant-specific) authentication strength from a source policy, if any.
 * Built-in strengths are carried on the template; custom ones are not.
 * @param {object} rawPolicy
 * @returns {{ id: string, displayName: string|null } | null}
 */
export const getUnsupportedAuthenticationStrength = (rawPolicy) => {
  const authenticationStrength = rawPolicy?.grantControls?.authenticationStrength
  const authStrengthId = authenticationStrength?.id
  if (
    !authStrengthId ||
    isGlobalAuthStrength(authStrengthId) ||
    isAuthenticationStrengthRequirementRef(authStrengthId)
  ) {
    return null
  }

  return {
    id: authStrengthId,
    displayName: authenticationStrength.displayName || null,
  }
}

/**
 * Transform values to an array so it can be looped the same way as others
 * Currently all input values are either an string or an array of strings
 */
export const toArray = (values) => {
  if (!values) {
    return []
  }
  if (values && !Array.isArray(values)) {
    return [values]
  }
  return values
}

// A Conditional Access policy that targets service principals is a workload identity policy.
// Microsoft only accepts those with every cloud app, no user targeting, no user/sign-in/insider
// risk, and Block as the grant — so a template that breaks any of these could never deploy.
export const WORKLOAD_IDENTITY_TEMPLATE_ISSUES = {
  NEEDS_INCLUDED_SERVICE_PRINCIPALS: 'POLICY.VALIDATION.WORKLOAD_IDENTITY.NEEDS_INCLUDED_SERVICE_PRINCIPALS',
  APPLICATIONS_MUST_BE_ALL: 'POLICY.VALIDATION.WORKLOAD_IDENTITY.APPLICATIONS_MUST_BE_ALL',
  NO_USER_TARGETING: 'POLICY.VALIDATION.WORKLOAD_IDENTITY.NO_USER_TARGETING',
  NO_USER_RISK_CONDITIONS: 'POLICY.VALIDATION.WORKLOAD_IDENTITY.NO_USER_RISK_CONDITIONS',
  GRANT_MUST_BE_BLOCK: 'POLICY.VALIDATION.WORKLOAD_IDENTITY.GRANT_MUST_BE_BLOCK',
}

const SERVICE_PRINCIPAL_INPUTS = [INPUT_KEYS.includeServicePrincipals, INPUT_KEYS.excludeServicePrincipals]

const USER_TARGETING_INPUTS = [
  INPUT_KEYS.includeGroups,
  INPUT_KEYS.excludeGroups,
  INPUT_KEYS.includeRoles,
  INPUT_KEYS.excludeRoles,
  INPUT_KEYS.includeGuestsOrExternalUsers,
  INPUT_KEYS.excludeGuestsOrExternalUsers,
  INPUT_KEYS.excludeUsers,
]

const USER_RISK_INPUTS = [INPUT_KEYS.userRiskLevels, INPUT_KEYS.signInRiskLevels, INPUT_KEYS.insiderRiskLevels]

const NO_USERS = 'None'

const hasPolicyValue = (policyBody, input) => toArray(get(policyBody, INPUT_POLICY_PATH[input])).length > 0

// "Set" means it carries a value now or is left for the deploying technician to fill in.
const isInputSet = (policyBody, promptedInputs, input) =>
  hasPolicyValue(policyBody, input) || promptedInputs.includes(input)

export const isWorkloadIdentityPolicy = (policyBody, promptedInputs = []) =>
  SERVICE_PRINCIPAL_INPUTS.some((input) => isInputSet(policyBody, promptedInputs, input))

// Returns the message id of every rule a workload identity template breaks; empty when the
// template isn't a workload identity policy or is valid.
export const getWorkloadIdentityTemplateIssues = (policyBody, promptedInputs = []) => {
  if (!isWorkloadIdentityPolicy(policyBody, promptedInputs)) {
    return []
  }

  const issues = []

  if (!isInputSet(policyBody, promptedInputs, INPUT_KEYS.includeServicePrincipals)) {
    issues.push(WORKLOAD_IDENTITY_TEMPLATE_ISSUES.NEEDS_INCLUDED_SERVICE_PRINCIPALS)
  }

  const includeApplications = toArray(get(policyBody, INPUT_POLICY_PATH[INPUT_KEYS.includeApplications]))
  const applicationsAreAll = includeApplications.length === 1 && includeApplications[0] === INCLUDE_APPLICATIONS_ALL
  if (!applicationsAreAll || promptedInputs.includes(INPUT_KEYS.includeApplications)) {
    issues.push(WORKLOAD_IDENTITY_TEMPLATE_ISSUES.APPLICATIONS_MUST_BE_ALL)
  }

  const includeUsers = toArray(get(policyBody, INPUT_POLICY_PATH[INPUT_KEYS.includeUsers]))
  const targetsUsers =
    includeUsers.some((value) => value !== NO_USERS) ||
    promptedInputs.includes(INPUT_KEYS.includeUsers) ||
    USER_TARGETING_INPUTS.some((input) => isInputSet(policyBody, promptedInputs, input))
  if (targetsUsers) {
    issues.push(WORKLOAD_IDENTITY_TEMPLATE_ISSUES.NO_USER_TARGETING)
  }

  if (USER_RISK_INPUTS.some((input) => isInputSet(policyBody, promptedInputs, input))) {
    issues.push(WORKLOAD_IDENTITY_TEMPLATE_ISSUES.NO_USER_RISK_CONDITIONS)
  }

  const builtInControls = toArray(get(policyBody, INPUT_POLICY_PATH[INPUT_KEYS.builtInControls]))
  const grantIsBlock = builtInControls.length === 1 && builtInControls[0] === BUILT_IN_CONTROLS.BLOCK
  if (!grantIsBlock || promptedInputs.includes(INPUT_KEYS.builtInControls)) {
    issues.push(WORKLOAD_IDENTITY_TEMPLATE_ISSUES.GRANT_MUST_BE_BLOCK)
  }

  return issues
}

export const AUTHENTICATION_CONTEXT_TEMPLATE_ISSUES = {
  NO_CLOUD_APPS: 'POLICY.VALIDATION.AUTHENTICATION_CONTEXT.NO_CLOUD_APPS',
}

// Microsoft rejects a policy that targets cloud apps and an authentication context together — a
// context replaces cloud apps as the policy's target. "None" is the empty-policy placeholder, not
// a cloud app.
const targetsCloudApplications = (applications = {}) =>
  toArray(applications.includeApplications).some((value) => value !== INCLUDE_APPLICATIONS_NONE) ||
  toArray(applications.excludeApplications).length > 0

// Returns the message id of every rule a template that targets an authentication context breaks;
// empty when it targets no authentication context or is valid. A field left for the deploying
// technician to fill in counts as set, since they could fill it with cloud apps.
export const getAuthenticationContextTemplateIssues = (policyBody, promptedInputs = []) => {
  if (!isInputSet(policyBody, promptedInputs, INPUT_KEYS.includeAuthenticationContextClassReferences)) {
    return []
  }

  const targetsApplications =
    targetsCloudApplications({
      includeApplications: get(policyBody, INPUT_POLICY_PATH[INPUT_KEYS.includeApplications]),
      excludeApplications: get(policyBody, INPUT_POLICY_PATH[INPUT_KEYS.excludeApplications]),
    }) ||
    promptedInputs.includes(INPUT_KEYS.includeApplications) ||
    promptedInputs.includes(INPUT_KEYS.excludeApplications)

  return targetsApplications ? [AUTHENTICATION_CONTEXT_TEMPLATE_ISSUES.NO_CLOUD_APPS] : []
}

// Decodes legacy bitmask values for multi-select enums into arrays of strings
const decodeLegacyMultiValueEnumBitmask = (input, rawValue) => {
  const values = LEGACY_MULTI_VALUE_ENUM_BITMASK_VALUES[input] ?? []
  const numericValue = Number(rawValue)

  if (!Number.isInteger(numericValue) || numericValue <= 0) {
    return []
  }

  return values.filter((value, index) => {
    const flagValue = 2 ** index
    return Math.floor(numericValue / flagValue) % 2 === 1
  })
}

// Normalizes legacy multi-value enum inputs that may be stored as bitmasks or comma-separated strings into consistent arrays of strings for use in the form
const normalizeLegacyMultiValueEnum = (input, rawValue) => {
  if (rawValue == null) {
    return rawValue
  }

  if (Array.isArray(rawValue)) {
    return rawValue.flatMap((value) => {
      const normalizedValue = normalizeLegacyMultiValueEnum(input, value)

      if (normalizedValue == null) {
        return []
      }

      return Array.isArray(normalizedValue) ? normalizedValue : [normalizedValue]
    })
  }

  if (typeof rawValue === 'number') {
    return decodeLegacyMultiValueEnumBitmask(input, rawValue)
  }

  if (typeof rawValue === 'string') {
    const trimmedValue = rawValue.trim()

    if (!trimmedValue) {
      return []
    }

    // Rollback payloads can surface the legacy numeric form as strings, so handle that before splitting.
    if (/^\d+$/.test(trimmedValue)) {
      return decodeLegacyMultiValueEnumBitmask(input, trimmedValue)
    }

    return trimmedValue
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  }

  return rawValue
}

/**
 * Verify if input values have tenant specific properties
 */
export const hasTenantSpecificValues = (input, value, globalFormOptionsMap) => {
  // ServicePrincipalsInMyTenant is the same selector in every tenant, unlike a service principal id.
  const arrayValues = toArray(value).filter(
    (val) => val !== true && val !== false && typeof val !== 'number' && val !== ALL_SERVICE_PRINCIPALS_IN_TENANT,
  )

  // An appId is the same in every tenant the application is installed in, so it is kept as-is
  // rather than prompted for.
  if (!arrayValues?.length || isApplicationInput(input)) {
    return false
  }

  if (TENANT_SPECIFIC_ID_INPUTS.has(input)) {
    return arrayValues.some((val) => !isAnyRequirementRef(val))
  }

  const optionMap = globalFormOptionsMap?.[input]
  if (!optionMap || Object.keys(optionMap).length === 0) {
    return false
  }

  return arrayValues.some((val) => !optionMap[val] && !isAnyRequirementRef(val))
}

const AUTHENTICATION_CONTEXT_ID_PATTERN = /^c\d+$/i

// Returns the value an authentication context keeps in a template, or null when it can't be kept.
const toPortableAuthenticationContext = (value) => {
  if (isAuthenticationContextRequirementRef(value)) {
    return value
  }

  const name = typeof value === 'string' ? value.trim() : ''
  if (!name || AUTHENTICATION_CONTEXT_ID_PATTERN.test(name)) {
    return null
  }

  return toAuthenticationContextRequirementRef(name)
}

// A custom authentication factor's value is its name — the only identifier Graph has for a custom
// control — so any non-empty value (copied, imported, or typed with "Set in template") becomes a
// portable "customFactor:<name>" requirement, matched by name at deploy.
const toPortableCustomAuthenticationFactor = (value) => {
  if (isCustomAuthenticationFactorRequirementRef(value)) {
    return value
  }

  const name = typeof value === 'string' ? value.trim() : ''
  return name ? toCustomAuthenticationFactorRequirementRef(name) : null
}

const INCLUDE_ONLY_APPLICATIONS = new Set(
  BASE_INCLUDE_APPLICATIONS.filter(({ value }) => !BASE_APPLICATIONS.some((app) => app.value === value)).map(
    ({ value }) => value,
  ),
)

/**
 * Removes tenant specific values from the policy object for the templates
 */
export const filterTenantProperties = (rawPolicy, { keepCustomAuthenticationFactors = false } = {}) => {
  const policy = cloneDeep(rawPolicy || {})

  const toMap = (array) => new Map(array.map((item) => [item.value, true]))

  const GLOBAL_INCLUDE_USERS = toMap(BASE_INCLUDE_USERS)

  const GLOBAL_EXCLUDE_USERS = toMap(BASE_USERS)

  const GLOBAL_INCLUDE_LOCATIONS = toMap(BASE_INCLUDE_LOCATIONS)

  const GLOBAL_EXCLUDE_LOCATIONS = toMap(BASE_LOCATIONS)

  // Users - no globals besides the sentinels below exist for a specific user, so every raw
  // tenant id is stripped except a user requirement reference ("user:<upn>"), which is portable
  // across tenants by design and must survive templating (AC1) — mirrors how includeGroups/
  // excludeGroups retain "group:<id>" below.
  if (policy?.conditions?.users?.includeUsers?.length > 0) {
    policy.conditions.users.includeUsers = policy.conditions.users.includeUsers.filter(
      (user) => GLOBAL_INCLUDE_USERS.get(user) || isUserRequirementRef(user),
    )
  }

  if (policy?.conditions?.users?.excludeUsers?.length > 0) {
    policy.conditions.users.excludeUsers = policy.conditions.users.excludeUsers.filter(
      (user) => GLOBAL_EXCLUDE_USERS.get(user) || isUserRequirementRef(user),
    )
  }

  // Groups - no globals exist, remove all properties except group requirement references
  // ("group:<id>"), which are portable across tenants by design and must survive templating.
  if (policy?.conditions?.users?.includeGroups?.length > 0) {
    policy.conditions.users.includeGroups = policy.conditions.users.includeGroups.filter(isGroupRequirementRef)
  }

  if (policy?.conditions?.users?.excludeGroups?.length > 0) {
    policy.conditions.users.excludeGroups = policy.conditions.users.excludeGroups.filter(isGroupRequirementRef)
  }

  // Guest or external users - external tenant ids are globally unique, so an enumerated tenant list
  // is portable as-is and is kept exactly as authored. Only the Graph type annotation is dropped;
  // deploy names it again from membershipKind.
  GUEST_OR_EXTERNAL_USER_POLICY_KEYS.forEach((key) => {
    delete policy?.conditions?.users?.[key]?.externalTenants?.['@odata.type']
  })

  // Locations (include has global properties and exclude has no globals) — a named location
  // requirement reference ("location:<id>") is portable across tenants by design and must survive
  // templating, same as a group requirement reference.
  if (policy?.conditions?.locations?.includeLocations?.length > 0) {
    policy.conditions.locations.includeLocations = policy.conditions.locations.includeLocations.filter(
      (loc) => GLOBAL_INCLUDE_LOCATIONS.get(loc) || isLocationRequirementRef(loc),
    )
  }

  if (policy?.conditions?.locations?.excludeLocations?.length > 0) {
    policy.conditions.locations.excludeLocations = policy.conditions.locations.excludeLocations.filter(
      (loc) => GLOBAL_EXCLUDE_LOCATIONS.get(loc) || isLocationRequirementRef(loc),
    )
  }

  // Applications are kept as they are. An appId is the same in every tenant the application is
  // installed in, so deploy uses it as-is and drops it when the target tenant doesn't have it; an
  // application requirement reference ("application:<id>") is resolved by name at deploy. Only the
  // include-only selectors (All, None) are removed from excludeApplications.
  if (policy?.conditions?.applications?.excludeApplications?.length > 0) {
    policy.conditions.applications.excludeApplications = policy.conditions.applications.excludeApplications.filter(
      (app) => !INCLUDE_ONLY_APPLICATIONS.has(app),
    )
  }

  // grant controls - with policy requirements on, custom authentication factors are carried by name
  // (AC1). Without it they are stripped as before, since nothing could resolve them at deploy.
  if (policy?.grantControls?.customAuthenticationFactors) {
    policy.grantControls.customAuthenticationFactors = keepCustomAuthenticationFactors
      ? [...new Set(policy.grantControls.customAuthenticationFactors.map(toPortableCustomAuthenticationFactor))].filter(
          Boolean,
        )
      : []
  }

  if (policy?.grantControls?.termsOfUse) {
    policy.grantControls.termsOfUse = []
  }

  if (policy?.sessionControls?.globalSecureAccessFilteringProfile?.profileId) {
    policy.sessionControls.globalSecureAccessFilteringProfile.profileId = null
  }

  // Authentication contexts - a raw c1–c99 id means a different context in every tenant, so it's
  // stripped. An authentication context requirement reference ("authContext:<name>") is portable
  // by name and must survive templating (AC1), and so is a name typed into the field with "Set in
  // template" — it becomes that same requirement, matched by name at deploy.
  if (policy?.conditions?.applications?.includeAuthenticationContextClassReferences?.length > 0) {
    policy.conditions.applications.includeAuthenticationContextClassReferences = [
      ...new Set(
        policy.conditions.applications.includeAuthenticationContextClassReferences
          .map(toPortableAuthenticationContext)
          .filter(Boolean),
      ),
    ]
  }

  // Service principal object ids are tenant-specific. ServicePrincipalsInMyTenant and a
  // "servicePrincipal:<name>" requirement are portable and survive templating.
  const isPortableServicePrincipal = (value) =>
    value === ALL_SERVICE_PRINCIPALS_IN_TENANT || isServicePrincipalRequirementRef(value)

  if (policy?.conditions?.clientApplications?.includeServicePrincipals?.length > 0) {
    policy.conditions.clientApplications.includeServicePrincipals =
      policy.conditions.clientApplications.includeServicePrincipals.filter(isPortableServicePrincipal)
  }

  if (policy?.conditions?.clientApplications?.excludeServicePrincipals?.length > 0) {
    policy.conditions.clientApplications.excludeServicePrincipals =
      policy.conditions.clientApplications.excludeServicePrincipals.filter(isPortableServicePrincipal)
  }

  const GLOBAL_DEVICES = toMap([{ value: 'All' }, { value: 'Compliant' }, { value: 'DomainJoined' }])

  if (policy?.conditions?.devices?.includeDevices?.length > 0) {
    policy.conditions.devices.includeDevices = policy.conditions.devices.includeDevices.filter((device) =>
      GLOBAL_DEVICES.get(device),
    )
  }

  if (policy?.conditions?.devices?.excludeDevices?.length > 0) {
    policy.conditions.devices.excludeDevices = policy.conditions.devices.excludeDevices.filter((device) =>
      GLOBAL_DEVICES.get(device),
    )
  }

  const authStrengthId = policy?.grantControls?.authenticationStrength?.id
  if (authStrengthId) {
    // A built-in strength's id is global, and a custom strength requirement ("authStrength:<name>")
    // is portable by name — both survive templating. Any other id is one tenant's own strength.
    if (isGlobalAuthStrength(authStrengthId) || isAuthenticationStrengthRequirementRef(authStrengthId)) {
      // clean rest of the fields, keep only the id
      policy.grantControls.authenticationStrength = { id: authStrengthId }
    } else {
      // tenant specific auth strength
      policy.grantControls.authenticationStrength = null
    }
  }

  return policy
}

// helper for the fillMinimumRequiredValues method
// sets the default value of a given path into the policy
const setPolicyDefault = (policy, path) => {
  set(policy, path, get(DEFAULT_EMPTY_POLICY, path))
}

/**
 * add defaults for minimum required fields that are empty
 * https://learn.microsoft.com/en-us/graph/api/conditionalaccessroot-post-policies?view=graph-rest-1.0&tabs=http#request-body
 * @param {object} rawPolicy
 */
export const fillMinimumRequiredValues = (rawPolicy) => {
  const policy = cloneDeep(rawPolicy || {})

  // for the users and applications it only matters for MS if we have one include rule in it
  // if policy has only excludes is still invalid for MS
  if (
    !policy?.conditions?.users?.includeUsers?.length &&
    !policy?.conditions?.users?.includeGroups?.length &&
    !policy?.conditions?.users?.includeRoles?.length &&
    !policy?.conditions?.users?.includeGuestsOrExternalUsers
  ) {
    setPolicyDefault(policy, 'conditions.users.includeUsers')
  }

  if (
    !policy?.conditions?.applications?.includeApplications?.length &&
    !policy?.conditions?.applications?.includeAuthenticationContextClassReferences?.length &&
    !policy?.conditions?.applications?.includeUserActions?.length
  ) {
    setPolicyDefault(policy, 'conditions.applications.includeApplications')
  }

  if (!policy?.conditions?.clientAppTypes?.length) {
    setPolicyDefault(policy, 'conditions.clientAppTypes')
  }

  if (!policy?.grantControls && !policy.sessionControls) {
    setPolicyDefault(policy, 'grantControls')
  }

  return policy
}

export const cleanPolicyObject = (policy, options = {}) => {
  const { removeEmptyValues = true } = options
  const result = cloneDeep(policy)

  // Housekeeping items
  // make sure to delete these properties even if they are null
  delete result.id
  delete result.createdDateTime
  delete result.modifiedDateTime
  delete result.displayName
  delete result.templateId
  GUEST_OR_EXTERNAL_USER_POLICY_KEYS.forEach((key) => {
    delete result?.conditions?.users?.[key]?.externalTenants?.['@odata.type']
  })

  // Cleanup the top level of the policy
  Object.keys(result).forEach((body) => {
    if (removeEmptyValues && result[body] === null) {
      delete result[body]
    }
  })

  // Cleanup the conditions
  if (result.conditions) {
    Object.keys(result.conditions).forEach((condition) => {
      if (result.conditions[condition]) {
        Object.keys(result.conditions[condition]).forEach((key) => {
          // If any of a condition's children are empty then remove them
          if (
            removeEmptyValues &&
            Array.isArray(result.conditions[condition][key]) &&
            result.conditions[condition][key]?.length === 0
          ) {
            delete result.conditions[condition][key]

            // If any of a condition's children are null then remove them
          } else if (removeEmptyValues && result.conditions[condition][key] === null) {
            delete result.conditions[condition][key]
          }
        })

        // If the condition has no children then remove it
        if (removeEmptyValues && Object.keys(result.conditions[condition])?.length === 0) {
          delete result.conditions[condition]
        }

        // If the condition is null then remove it
      } else if (removeEmptyValues && result.conditions[condition] === null) {
        delete result.conditions[condition]
      }
    })
  }

  // Clean up grant controls
  if (result.grantControls) {
    if (result.grantControls['authenticationStrength@odata.context']) {
      // @TODO: Do we need to update the backend validation for this field instead of pruning it?
      delete result.grantControls['authenticationStrength@odata.context']
    }

    const authStrengthId = result?.grantControls?.authenticationStrength?.id
    if (authStrengthId) {
      // clean rest of the authenticationStrength fields, keep only the id
      result.grantControls.authenticationStrength = { id: authStrengthId }
    }

    Object.keys(result.grantControls).forEach((control) => {
      if (
        removeEmptyValues &&
        Array.isArray(result.grantControls[control]) &&
        result.grantControls[control]?.length === 0
      ) {
        delete result.grantControls[control]
      } else if (removeEmptyValues && result.grantControls[control] === null) {
        delete result.grantControls[control]
      }
    })
  }

  return result
}

/**
 * Transform a raw policy into our form object
 * This function is the inverse transformation of buildPolicyPayload
 * @param {*} rawPolicy
 */
// Risk level conditions Microsoft stores as one comma-separated string rather than an array.
const RISK_LEVEL_STRING_INPUTS = [INPUT_KEYS.insiderRiskLevels, INPUT_KEYS.agentIdRiskLevels]

export const toFormValues = (rawPolicy) => {
  const formValues = {}

  INPUT_LIST.forEach((input) => {
    const inputValue = get(rawPolicy, INPUT_POLICY_PATH[input])

    let parsedValue = inputValue
    // Microsoft often uses comma-separated strings; history/API snapshots may deserialize the same fields as arrays
    // or legacy numeric flagged enums.
    if (
      [
        INPUT_KEYS.transferMethods,
        INPUT_KEYS.includeGuestsOrExternalUsers,
        INPUT_KEYS.excludeGuestsOrExternalUsers,
      ].includes(input)
    ) {
      parsedValue = normalizeLegacyMultiValueEnum(input, inputValue)
    } else if (RISK_LEVEL_STRING_INPUTS.includes(input)) {
      if (Array.isArray(inputValue)) {
        parsedValue = inputValue
      } else if (typeof inputValue === 'string') {
        // An empty string is "no levels", not one blank level.
        parsedValue = inputValue
          .split(',')
          .map((level) => level.trim())
          .filter(Boolean)
      }
    } else if (input === INPUT_KEYS.includeUserActions) {
      // MS has includeUserActions as an array but only allows selecting one option
      // so in our app it's formatted as a single select input (string)
      parsedValue = inputValue?.[0]
    }

    // A collection Graph has not set is [], and an older snapshot may still say null.
    // Multi-selects always call values.map, so the form gets the empty array those controls expect.
    if (parsedValue === null && Array.isArray(INPUT_TO_EMPTY_VALUE[input])) {
      parsedValue = []
    }

    if (parsedValue !== undefined) {
      formValues[input] = parsedValue
    }
  })

  if (rawPolicy?.displayName !== undefined) {
    formValues.policyName = rawPolicy.displayName
  }

  return formValues
}

/**
 * External tenants for a guest/external user condition, with membershipKind kept in step with the
 * tenant list: tenant ids target those tenants only (enumerated), no tenant ids target every
 * external tenant (all). Graph requires membershipKind whenever guestOrExternalUserTypes is set.
 */
const toExternalTenants = (externalTenants) => {
  const { members, ...rest } = externalTenants ?? {}

  if (members?.length) {
    return { ...rest, membershipKind: EXTERNAL_TENANTS_MEMBERSHIP_KIND.ENUMERATED, members }
  }

  if (!rest.membershipKind || rest.membershipKind === EXTERNAL_TENANTS_MEMBERSHIP_KIND.ENUMERATED) {
    return { ...rest, membershipKind: EXTERNAL_TENANTS_MEMBERSHIP_KIND.ALL }
  }

  return rest
}

/**
 * Apply the filled form values to the json policy object
 * This function is the inverse transformation of toFormValues
 * @param {*} rawPolicy
 * @param {*} formValues
 * @returns the updated policy
 */
export const buildPolicyPayload = (rawPolicy = {}, formValues, options = {}) => {
  const policy = cloneDeep(rawPolicy)

  // build policy skeleton (if empty)
  if (!policy.conditions) {
    policy.conditions = {}
  }

  if (!policy.conditions.users) {
    policy.conditions.users = {}
  }

  if (!policy.conditions.locations) {
    policy.conditions.locations = {}
  }

  if (!policy.conditions.applications) {
    policy.conditions.applications = {}
  }

  // replace the policy values with the form values
  INPUT_LIST.forEach((input) => {
    const value = formValues?.[input]

    // if value is undefined means input is not in the template so ignore
    // however if value is null or an empty array we should apply it
    if (value !== undefined) {
      let path = INPUT_POLICY_PATH[input]
      let formattedValue = value

      if (
        [
          INPUT_KEYS.insiderRiskLevels,
          INPUT_KEYS.agentIdRiskLevels,
          INPUT_KEYS.transferMethods,
          INPUT_KEYS.includeGuestsOrExternalUsers,
          INPUT_KEYS.excludeGuestsOrExternalUsers,
        ].includes(input)
      ) {
        // microsoft formats these inputs as strings of comma separated values, instead of being array for some reason
        formattedValue = value.join(',')
        // Graph has no empty risk level, so a cleared risk condition is sent as unset.
        if (RISK_LEVEL_STRING_INPUTS.includes(input) && !value.length) {
          formattedValue = null
        }
      } else if (input === INPUT_KEYS.authenticationStrength && value === null) {
        // if authentication strength is unselected, object needs to be null
        path = 'grantControls.authenticationStrength'
      } else if (input === INPUT_KEYS.includeUserActions) {
        // MS has includeUserActions as an array but only allows selecting one option
        // so in our app it's formatted as a single select input (string)
        formattedValue = value ? [value] : []
      }

      // Leave a cleared collection as []. Null is not valid for these properties, and Graph
      // rejects the whole update with error 1007.
      set(policy, path, formattedValue)
    }
  })

  // locations object has to be null if no location is set
  const includeLocations = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.includeLocations])
  const excludeLocations = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.excludeLocations])
  if (isEmpty(includeLocations) && isEmpty(excludeLocations)) {
    policy.conditions.locations = null
  }

  // authenticationFlows object has to be null if no transferMethods is set
  const transferMethods = get(policy, INPUT_POLICY_PATH[INPUT_KEYS.transferMethods])
  if (!transferMethods) {
    policy.conditions.authenticationFlows = null
  }

  GUEST_OR_EXTERNAL_USER_POLICY_KEYS.forEach((key) => {
    // object has to be null if no guestOrExternalUserTypes set
    if (!get(policy, INPUT_POLICY_PATH[key])) {
      policy.conditions.users[key] = null
      return
    }

    policy.conditions.users[key].externalTenants = toExternalTenants(policy.conditions.users[key].externalTenants)
  })

  // clean policy
  const formattedPolicy = cleanPolicyObject(policy, options)
  pruneEmptyMappedPolicyObjects(formattedPolicy, options)

  // add name and status to policy
  formattedPolicy.displayName = formValues?.policyName || policy.displayName
  formattedPolicy.state = formValues?.policyStatus || policy.state

  return formattedPolicy
}

// These defaults can be added even when the historical snapshot never had them, so remove them if they were not present in the original raw policy and are empty in the rollback payload
const ROLLBACK_DEFAULT_PATHS = [
  'conditions.users.includeGuestsOrExternalUsers',
  'conditions.users.excludeGuestsOrExternalUsers',
  'conditions.authenticationFlows',
  'conditions.locations',
  'conditions.applications',
  'conditions.users',
]

// Explicit nulls still count as part of the saved snapshot, so only treat truly missing paths as absent
const hasPolicyPath = (policy, path) => {
  let currentValue = policy

  return path.split('.').every((pathPart) => {
    if (currentValue == null || !Object.prototype.hasOwnProperty.call(currentValue, pathPart)) {
      return false
    }

    currentValue = currentValue[pathPart]
    return true
  })
}

// Drop builder defaults so the rollback payload stays aligned with the selected history entry
const stripRollbackDefaults = (rollbackPayload, rawPolicy) => {
  const trimmedRollbackPayload = cloneDeep(rollbackPayload)

  ROLLBACK_DEFAULT_PATHS.forEach((path) => {
    const rollbackContainer = get(trimmedRollbackPayload, path)

    if (hasPolicyPath(rawPolicy, path) || !isEmpty(rollbackContainer)) {
      return
    }

    const pathParts = path.split('.')
    const key = pathParts.pop()
    const parent = get(trimmedRollbackPayload, pathParts.join('.'))

    if (parent && key) {
      delete parent[key]
    }
  })

  if (!rawPolicy?.conditions && isEmpty(trimmedRollbackPayload?.conditions)) {
    delete trimmedRollbackPayload.conditions
  }

  return trimmedRollbackPayload
}

export const buildReviewPayloadFromPolicy = (rawPolicy) =>
  buildPolicyPayload(buildPolicyPayload(rawPolicy, {}), toFormValues(rawPolicy), { removeEmptyValues: false })

// Rollback should submit the exact historical snapshot, while still normalizing any legacy field shapes
// that the current editor expects to round-trip through the API payload.
export const buildRollbackPayload = (rawPolicy = {}) => {
  const cleanedRawPolicy = cleanPolicyObject(cloneDeep(rawPolicy), { removeEmptyValues: false })
  const rollbackPayload = buildPolicyPayload(cleanedRawPolicy, toFormValues(rawPolicy), {
    removeEmptyValues: false,
  })

  return stripRollbackDefaults(rollbackPayload, cleanedRawPolicy)
}

/**
 * Build the policy payload for the template by filtering tenant specific values before
 * 1. filter specific tenant properties that would make deploy fail on a different tenant
 * 2. fill minimum required values with defaults in case they were filtered
 * 3. build policy payload merging form values into the rawPolicy
 */
export const buildPolicyTemplatePayload = (rawPolicy, formValues, filterOptions) =>
  buildPolicyPayload(fillMinimumRequiredValues(filterTenantProperties(rawPolicy, filterOptions)), formValues)

/**
 * Removes non tenant specific values from policyConfigurations to be used in the template forms
 * @param {object} policyConfigurations
 * @returns the policy configurations with only non tenant specific values
 */
export const sanitizePolicyConfigurations = (policyConfigurations) => ({
  idToNameMappings: {
    directoryRoles: policyConfigurations?.idToNameMappings?.directoryRoles,
  },
  authenticationStrengths: policyConfigurations?.authenticationStrengths?.filter(({ authStrengthId }) =>
    isGlobalAuthStrength(authStrengthId),
  ),
})

const getAuthenticationStrengthFormOptions = (authenticationStrengths = []) => {
  const authStrengthsById = new Map(
    BUILT_IN_AUTHENTICATION_STRENGTHS.map((authStrength) => [authStrength.authStrengthId, authStrength]),
  )

  authenticationStrengths.forEach((authStrength) => {
    const authStrengthId = authStrength?.authStrengthId
    if (!authStrengthId) {
      return
    }

    authStrengthsById.set(authStrengthId, {
      ...authStrengthsById.get(authStrengthId),
      ...authStrength,
    })
  })

  return Array.from(authStrengthsById.values()).map((authStrength) => ({
    label: authStrength.displayName,
    value: authStrength.authStrengthId,
  }))
}

/**
 * Creates the options to populate the Select Dropdowns of the inputs
 * @param {object} integratedEmployeesMetaData
 * @param {object} policyConfigurations
 * @param {object} intl
 * @returns the form data with the options for each input
 */
// The tenant's own authentication contexts, labelled by name, plus any id already used by a policy
// that the list doesn't cover (e.g. the list failed to load), so an existing value never disappears.
const buildAuthenticationContextOptions = (authenticationContexts, usedOptions) => {
  const contextOptions = authenticationContexts
    .filter((context) => context?.id)
    .map((context) => ({ label: context.displayName || context.id, value: context.id }))
  const knownIds = new Set(contextOptions.map((option) => option.value))

  return [...contextOptions, ...usedOptions.filter((option) => !knownIds.has(option.value))]
}

// The tenant's own service principals, labelled by name, plus any id already used by a policy that
// the list doesn't cover (e.g. the list failed to load), so an existing value never disappears.
// Include also offers Graph's "every service principal in the tenant" selector.
const buildServicePrincipalOptions = (servicePrincipals, usedOptions, { allLabel = null } = {}) => {
  const servicePrincipalOptions = servicePrincipals
    .filter((servicePrincipal) => servicePrincipal?.id)
    .map((servicePrincipal) => ({
      label: servicePrincipal.displayName || servicePrincipal.id,
      value: servicePrincipal.id,
    }))
    .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }))
  const allOption = allLabel ? [{ label: allLabel, value: ALL_SERVICE_PRINCIPALS_IN_TENANT }] : []
  const knownIds = new Set([...allOption, ...servicePrincipalOptions].map((option) => option.value))

  return [...allOption, ...servicePrincipalOptions, ...usedOptions.filter((option) => !knownIds.has(option.value))]
}

// Graph has no list of custom authentication factors yet (AP-7322). The factors a tenant already
// uses are the ones on its policies. The MFA configure payload omits rawPolicy, so the policy list
// is what actually carries grantControls.customAuthenticationFactors.
const collectCustomAuthenticationFactors = (policies) => {
  const factors = {}

  policies?.forEach((policy) => {
    policy?.rawPolicy?.grantControls?.customAuthenticationFactors?.forEach((factor) => {
      if (typeof factor === 'string' && factor) {
        factors[factor] = true
      }
    })
  })

  return factors
}

export const getPolicyFormOptions = ({
  integratedEmployeesMetaData = {},
  policyConfigurations = {},
  policies = [],
  groups = [],
  namedLocations = [],
  authenticationContexts = [],
  servicePrincipals = [],
  intl,
}) => {
  const formOptions = {}
  const { users = [] } = integratedEmployeesMetaData || {}

  const formattedUserOptions = users.map((user) => ({
    ...user,
    label: `${user.label}${user.email ? ` (${user.email})` : ''}`,
  }))
  formOptions.includeUsers = [...BASE_INCLUDE_USERS, ...formattedUserOptions]
  formOptions.excludeUsers = [...BASE_USERS, ...formattedUserOptions]

  // conditions/Locations
  let locations = []
  const resolvedNamedLocations = namedLocations?.length > 0 ? namedLocations : (policyConfigurations?.locations ?? [])

  if (resolvedNamedLocations.length > 0) {
    locations = [...resolvedNamedLocations]
      .filter((location) => !shouldHideNamedLocation(location))
      .sort((left, right) =>
        (left?.displayName ?? '').localeCompare(right?.displayName ?? '', undefined, { sensitivity: 'base' }),
      )
      .map((location) => {
        const formattedLocation = {
          label: `${location?.displayName}` || '',
          value: location?.id || '',
        }

        if (location?.isTrusted === true) {
          formattedLocation.label = `${formattedLocation.label} (Trusted)`
        }

        return formattedLocation
      })
  }

  // Add globals to the top of the list
  formOptions.includeLocations = [...BASE_INCLUDE_LOCATIONS, ...locations]
  formOptions.excludeLocations = [...BASE_LOCATIONS, ...locations]

  // groups
  const allGroups = [...groups].sort((left, right) =>
    (left?.label ?? '').localeCompare(right?.label ?? '', undefined, { sensitivity: 'base' }),
  )
  formOptions.includeGroups = allGroups
  formOptions.excludeGroups = allGroups

  // roles
  const roles = Object.entries(policyConfigurations?.idToNameMappings?.directoryRoles || {}).map((entry) => ({
    value: entry[0],
    label: entry[1],
  }))
  formOptions.includeRoles = roles
  formOptions.excludeRoles = roles

  // conditions/Applications
  let applications = []
  if (policyConfigurations?.applications?.length > 0) {
    applications = policyConfigurations.applications.map((app) => {
      const formattedApp = {
        label: `${app?.applicationName}` || '',
        value: app?.applicationId || '',
      }

      return formattedApp
    })
  }

  const knownApplications = KNOWN_MICROSOFT_APPLICATIONS.filter(
    (knownApplication) => !applications.some((application) => application.value === knownApplication.value),
  )

  // Add globals to the top of the list
  formOptions.includeApplications = [...BASE_INCLUDE_APPLICATIONS, ...knownApplications, ...applications]
  formOptions.excludeApplications = [...BASE_APPLICATIONS, ...knownApplications, ...applications]

  formOptions.clientAppTypes = [
    {
      label: intl.formatMessage({ id: 'GENERAL.ALL' }),
      value: 'all',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_CONFIGURE_MODERN_AUTH_CLIENTS_BROWSER' }),
      value: 'browser',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_CONFIGURE_MODERN_AUTH_CLIENTS_MOBILE_AND_DESKTOP' }),
      value: 'mobileAppsAndDesktopClients',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_LEGACY_AUTH_CLIENTS_EXCHANGE_ACTIVESYNC' }),
      value: 'exchangeActiveSync',
    },
    {
      label: intl.formatMessage({ id: 'SECURE.CLIENT_APPS_LEGACY_AUTH_CLIENTS_OTHER' }),
      value: 'other',
    },
  ]

  formOptions.excludePlatforms = [
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.ANDROID' }),
      value: 'android',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.IOS' }),
      value: 'iOS',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.WINDOWS' }),
      value: 'windows',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.WINDOWS_PHONE' }),
      value: 'windowsPhone',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.MAC' }),
      value: 'macOS',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.PLATFORMS.LINUX' }),
      value: 'linux',
    },
  ]

  formOptions.includePlatforms = [
    {
      label: intl.formatMessage({ id: 'GENERAL.ALL' }),
      value: 'all',
    },
    ...formOptions.excludePlatforms,
  ]

  formOptions.userRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.HIGH' }),
      value: 'high',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MEDIUM' }),
      value: 'medium',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.LOW' }),
      value: 'low',
    },
  ]

  formOptions.signInRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.HIGH' }),
      value: 'high',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MEDIUM' }),
      value: 'medium',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.LOW' }),
      value: 'low',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SIGNIN_RISK.NO_RISK' }),
      value: 'none',
    },
  ]

  formOptions.insiderRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.ELEVATED' }),
      value: 'elevated',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MODERATE' }),
      value: 'moderate',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MINOR' }),
      value: 'minor',
    },
  ]

  formOptions.transferMethods = [
    {
      label: intl.formatMessage({ id: 'POLICY.TRANSFER_METHODS.DEVICE_CODE_FLOW' }),
      value: 'deviceCodeFlow',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.TRANSFER_METHODS.AUTH_TRANSFER' }),
      value: 'authenticationTransfer',
    },
  ]

  formOptions.includeGuestsOrExternalUsers = [
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.B2B_COLLAB_GUEST' }),
      value: 'b2bCollaborationGuest',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.B2B_COLLAB_MEMBER' }),
      value: 'b2bCollaborationMember',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.B2B_DIRECT_CONNECT' }),
      value: 'b2bDirectConnectUser',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.LOCAL_GUEST' }),
      value: 'internalGuest',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.SERVICE_PROVIDER' }),
      value: 'serviceProvider',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GUEST_USER.OTHER' }),
      value: 'otherExternalUser',
    },
  ]
  formOptions.excludeGuestsOrExternalUsers = formOptions.includeGuestsOrExternalUsers

  formOptions.includeUserActions = [
    {
      label: intl.formatMessage({ id: 'POLICY.USER_ACTIONS.REGISTER_SECURITY_INFO' }),
      value: 'urn:user:registersecurityinfo',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.USER_ACTIONS.REGISTER_JOIN_DEVICES' }),
      value: 'urn:user:registerdevice',
    },
  ]

  formOptions.builtInControls = [
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.BLOCK_ACCESS' }),
      value: 'block',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_MFA' }),
      value: 'mfa',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_COMPLIANT_DEVICE' }),
      value: 'compliantDevice',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_MS_ENTRA' }),
      value: 'domainJoinedDevice',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_APPROVED_CLIENT_APP' }),
      value: 'approvedApplication',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_APP_PROTECTION' }),
      value: 'compliantApplication',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_PASSWORD_CHANGE' }),
      value: 'passwordChange',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.GRANT_CONTROLS.REQUIRE_RISK_REMEDIATION' }),
      value: 'riskRemediation',
    },
  ]

  formOptions.grantControlsOperator = [
    {
      label: intl.formatMessage({ id: 'POLICY.OPERATOR.REQUIRE_ALL' }),
      value: 'AND',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.OPERATOR.REQUIRE_ONE' }),
      value: 'OR',
    },
  ]

  // AP-7322: Graph still has no custom-factor catalog. Options are the factors already on a tenant
  // policy, read from the policy list (rawPolicy) and from configure rows that include it.
  // A factor no policy uses yet will not appear here.
  const customAuthObj = {
    ...collectCustomAuthenticationFactors(policyConfigurations?.data),
    ...collectCustomAuthenticationFactors(policies),
  }
  formOptions.customAuthenticationFactors = Object.keys(customAuthObj).map((authFactor) => ({
    label: authFactor,
    value: authFactor,
  }))

  formOptions.authenticationStrength = getAuthenticationStrengthFormOptions(
    policyConfigurations?.authenticationStrengths,
  )

  formOptions.policyStatus = getPolicyStatusOptions(intl)

  const filterModeOptions = [
    {
      label: intl.formatMessage({ id: 'POLICY.FILTER_MODE.INCLUDE' }),
      value: 'include',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.FILTER_MODE.EXCLUDE' }),
      value: 'exclude',
    },
  ]
  formOptions.applicationFilterMode = filterModeOptions
  formOptions.deviceFilterMode = filterModeOptions
  formOptions.servicePrincipalFilterMode = filterModeOptions

  formOptions.servicePrincipalRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.HIGH' }),
      value: 'high',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MEDIUM' }),
      value: 'medium',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.LOW' }),
      value: 'low',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SIGNIN_RISK.NO_RISK' }),
      value: 'none',
    },
  ]

  formOptions.agentIdRiskLevels = [
    {
      label: intl.formatMessage({ id: 'GENERAL.HIGH' }),
      value: 'high',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.MEDIUM' }),
      value: 'medium',
    },
    {
      label: intl.formatMessage({ id: 'GENERAL.LOW' }),
      value: 'low',
    },
  ]

  formOptions.signInFrequencyType = [
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.HOURS' }),
      value: 'hours',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.DAYS' }),
      value: 'days',
    },
  ]

  formOptions.signInFrequencyInterval = [
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.TIME_BASED' }),
      value: 'timeBased',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.EVERY_TIME' }),
      value: 'everyTime',
    },
  ]

  formOptions.signInFrequencyAuthenticationType = [
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.PRIMARY_AND_SECONDARY' }),
      value: 'primaryAndSecondaryAuthentication',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.SECONDARY' }),
      value: 'secondaryAuthentication',
    },
  ]

  formOptions.persistentBrowserMode = [
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.ALWAYS' }),
      value: 'always',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.NEVER' }),
      value: 'never',
    },
  ]

  formOptions.cloudAppSecurityType = [
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.MCAS_CONFIGURED' }),
      value: 'mcasConfigured',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.MONITOR_ONLY' }),
      value: 'monitorOnly',
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.BLOCK_DOWNLOADS' }),
      value: 'blockDownloads',
    },
  ]

  formOptions.continuousAccessEvaluationMode = [
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.CAE_DISABLED' }),
      value: CONTINUOUS_ACCESS_EVALUATION_MODES.DISABLED,
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.CAE_STRICT_ENFORCEMENT' }),
      value: CONTINUOUS_ACCESS_EVALUATION_MODES.STRICT_ENFORCEMENT,
    },
    {
      label: intl.formatMessage({ id: 'POLICY.SESSION.CAE_STRICT_LOCATION' }),
      value: CONTINUOUS_ACCESS_EVALUATION_MODES.STRICT_LOCATION,
    },
  ]

  const deviceStateOptions = [
    { label: intl.formatMessage({ id: 'GENERAL.ALL' }), value: 'All' },
    { label: intl.formatMessage({ id: 'POLICY.DEVICES.COMPLIANT' }), value: 'Compliant' },
    { label: intl.formatMessage({ id: 'POLICY.DEVICES.DOMAIN_JOINED' }), value: 'DomainJoined' },
  ]
  formOptions.includeDevices = deviceStateOptions
  formOptions.excludeDevices = deviceStateOptions

  formOptions.includeAuthenticationContextClassReferences = buildAuthenticationContextOptions(
    authenticationContexts,
    collectUniquePolicyPathValues(
      policyConfigurations,
      INPUT_POLICY_PATH[INPUT_KEYS.includeAuthenticationContextClassReferences],
    ),
  )
  formOptions.includeServicePrincipals = buildServicePrincipalOptions(
    servicePrincipals,
    collectUniquePolicyPathValues(policyConfigurations, INPUT_POLICY_PATH[INPUT_KEYS.includeServicePrincipals]),
    { allLabel: intl.formatMessage({ id: 'POLICY.SERVICE_PRINCIPALS.ALL_OWNED' }) },
  )
  formOptions.excludeServicePrincipals = buildServicePrincipalOptions(
    servicePrincipals,
    collectUniquePolicyPathValues(policyConfigurations, INPUT_POLICY_PATH[INPUT_KEYS.excludeServicePrincipals]),
  )
  formOptions.termsOfUse = collectUniquePolicyPathValues(policyConfigurations, INPUT_POLICY_PATH[INPUT_KEYS.termsOfUse])
  // Tenant ids are global, so one used on either side of any of the tenant's policies is offered on both.
  // Read from the policy list: the configurations payload omits rawPolicy, so it never carries them.
  const externalTenantOptions = uniqBy(
    [INPUT_KEYS.includeExternalTenants, INPUT_KEYS.excludeExternalTenants].flatMap((input) =>
      collectUniquePolicyPathValues({ data: policies }, INPUT_POLICY_PATH[input]),
    ),
    'value',
  )
  formOptions.includeExternalTenants = externalTenantOptions
  formOptions.excludeExternalTenants = externalTenantOptions

  return formOptions
}

const mapValuesToLabels = (options) => {
  const result = {}

  options?.forEach((option) => {
    result[option.value] = option.label
  })

  return result
}

/**
 * Returns the form options in a map form { input: { value1: 'label1', value2: 'label2' }, ... }
 * @param {object} formOptions object containing the options for each input
 */
export const getPolicyFormOptionsMap = (formOptions) => {
  const formLabelsMap = {}

  INPUT_LIST.forEach((input) => {
    formLabelsMap[input] = mapValuesToLabels(formOptions?.[input])
  })

  return formLabelsMap
}

const hasGrantControls = (form) =>
  form?.builtInControls?.length ||
  form?.authenticationStrength ||
  form?.customAuthenticationFactors?.length ||
  form?.termsOfUse?.length

const hasUsersCondition = (form) =>
  form?.includeUsers?.length ||
  form?.includeGroups?.length ||
  form?.includeRoles?.length ||
  form?.includeGuestsOrExternalUsers?.length

const getTargetedPlatforms = (form) => {
  const includePlatforms = form?.includePlatforms ?? []
  const excludePlatforms = new Set(form?.excludePlatforms ?? [])
  const includesAllPlatforms = !includePlatforms.length || includePlatforms.includes(PLATFORM_VALUES.ALL)
  const targetedPlatforms = includesAllPlatforms ? ALL_DEVICE_PLATFORMS : includePlatforms

  return targetedPlatforms.filter((platform) => platform !== PLATFORM_VALUES.ALL && !excludePlatforms.has(platform))
}

const hasUnsupportedAppProtectionPlatforms = (form) =>
  form?.builtInControls?.includes(BUILT_IN_CONTROLS.COMPLIANT_APPLICATION) &&
  getTargetedPlatforms(form).some((platform) => !APP_PROTECTION_SUPPORTED_PLATFORMS.has(platform))

const hasUnsupportedApprovedClientAppPlatforms = (form) =>
  form?.builtInControls?.includes(BUILT_IN_CONTROLS.APPROVED_APPLICATION) &&
  getTargetedPlatforms(form).some((platform) => !APPROVED_CLIENT_APP_SUPPORTED_PLATFORMS.has(platform))

const hasInvalidPasswordChangeOperator = (form) =>
  isPasswordChangeSelected(form) && form?.grantControlsOperator !== GRANT_CONTROL_OPERATORS.AND

const EXTERNAL_TENANT_INPUTS = [
  { tenantsInput: INPUT_KEYS.includeExternalTenants, guestInput: INPUT_KEYS.includeGuestsOrExternalUsers },
  { tenantsInput: INPUT_KEYS.excludeExternalTenants, guestInput: INPUT_KEYS.excludeGuestsOrExternalUsers },
]

const EXTERNAL_TENANT_VALIDATIONS = EXTERNAL_TENANT_INPUTS.flatMap(({ tenantsInput, guestInput }) => [
  {
    input: tenantsInput,
    error: (form) => toArray(form?.[tenantsInput]).some((tenantId) => !isGuidFormat(tenantId)),
    errorMessage: 'POLICY.VALIDATION.EXTERNAL_TENANT_ID_FORMAT',
  },
  {
    input: tenantsInput,
    error: (form) => toArray(form?.[tenantsInput]).length > 0 && !toArray(form?.[guestInput]).length,
    errorMessage: 'POLICY.VALIDATION.EXTERNAL_TENANTS_REQUIRE_GUEST_TYPES',
  },
])

const POLICY_FORM_VALIDATIONS = [
  ...EXTERNAL_TENANT_VALIDATIONS,
  // conditions inputs
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) => !hasUsersCondition(form),
    errorMessage: 'POLICY.VALIDATION.USER_CONDITIONS_INCLUDE_REQUIRED',
  },
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) =>
      form?.includeUsers?.includes('None') &&
      (form?.includeUsers?.length > 1 || form?.includeGuestsOrExternalUsers?.length),
    errorMessage: 'POLICY.VALIDATION.USER_NONE_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) =>
      form?.includeUsers?.includes('All') &&
      !form?.includeUsers?.includes('None') && // Avoid 2 errors saying the same thing for 'All' and 'None'
      (form?.includeUsers?.length > 1 || form?.includeGuestsOrExternalUsers?.length),
    errorMessage: 'POLICY.VALIDATION.USER_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeUsers,
    error: (form) =>
      form?.includeUsers?.includes('GuestsOrExternalUsers') &&
      !form?.includeUsers?.includes('None') &&
      !form?.includeUsers?.includes('All') && // Avoid 2 errors saying the same thing for 'All' and 'None'
      form?.includeGuestsOrExternalUsers?.length,
    errorMessage: 'POLICY.VALIDATION.USER_GUEST_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeLocations,
    error: (form) => form?.includeLocations?.includes('All') && form?.includeLocations?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_LOCATIONS_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeLocations,
    error: (form) =>
      form?.includeLocations?.includes('AllTrusted') &&
      !form?.includeLocations?.includes('All') && // Avoid 2 errors saying the same thing for 'All' and 'AllTrusted'
      form?.includeLocations?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_LOCATIONS_ALL_TRUSTED_COMBINATION',
  },
  {
    input: INPUT_KEYS.excludeLocations,
    error: (form) => form?.excludeLocations?.includes('AllTrusted') && form?.excludeLocations?.length > 1,
    errorMessage: 'POLICY.VALIDATION.EXCLUDE_LOCATIONS_ALL_TRUSTED_COMBINATION',
  },
  {
    input: INPUT_KEYS.excludeLocations,
    // A field auto-resolved through a named location requirement carries only "location:<id>"
    // sentinels here, not real values yet — like a group requirement, whether it (and its paired
    // includeLocations) is actually valid is only known once it resolves at deploy time, so this
    // manual-form check defers to the deploy-time re-check (ReviewPanel's
    // hasExcludeLocationsMissingInclude / the backend's own guard) instead of blocking the
    // technician here before that resolution has even run.
    error: (form) =>
      form?.excludeLocations?.length &&
      !form.excludeLocations.every(isLocationRequirementRef) &&
      !form?.includeLocations?.length &&
      !hasImplicitIncludedLocations(form),
    errorMessage: 'POLICY.VALIDATION.LOCATIONS_NEED_INCLUDE',
  },
  {
    input: INPUT_KEYS.includeApplications,
    error: (form) => form?.includeApplications?.includes('None') && form?.includeApplications?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_APPLICATIONS_NONE_COMBINATION',
  },
  {
    input: INPUT_KEYS.includeApplications,
    error: (form) =>
      form?.includeApplications?.includes('All') &&
      !form?.includeApplications?.includes('None') && // Avoid 2 errors saying the same thing for 'All' and 'None'
      form?.includeApplications?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_APPLICATIONS_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.clientAppTypes,
    error: (form) => !form?.clientAppTypes?.length,
    errorMessage: 'POLICY.VALIDATION.CLIENT_APP_TYPES_REQUIRED',
  },
  {
    input: INPUT_KEYS.clientAppTypes,
    error: (form) => form?.clientAppTypes?.includes('all') && form?.clientAppTypes?.length > 1,
    errorMessage: 'POLICY.VALIDATION.CLIENT_APP_TYPES_ALL_COMBINATION',
  },
  {
    input: INPUT_KEYS.includePlatforms,
    error: (form) => form?.includePlatforms?.includes('all') && form?.includePlatforms?.length > 1,
    errorMessage: 'POLICY.VALIDATION.INCLUDE_PLATFORMS_ALL_COMBINATION',
  },
  // user actions
  // @TODO: when adding session controls to our form we should validate includeUserActions against it
  {
    input: INPUT_KEYS.includeUserActions,
    error: (form) =>
      form?.includeUserActions && (form?.includeApplications?.length || form?.excludeApplications?.length),
    errorMessage: 'POLICY.VALIDATION.USER_ACTIONS_AND_APPLICATIONS',
  },
  {
    input: INPUT_KEYS.includeAuthenticationContextClassReferences,
    error: (form) => form?.includeAuthenticationContextClassReferences?.length > 0 && targetsCloudApplications(form),
    errorMessage: 'POLICY.VALIDATION.AUTHENTICATION_CONTEXT_AND_APPLICATIONS',
  },
  {
    input: INPUT_KEYS.customAuthenticationFactors,
    error: (form) =>
      form?.includeUserActions === 'urn:user:registerdevice' && form?.customAuthenticationFactors?.length,
    errorMessage: 'POLICY.VALIDATION.USER_ACTIONS_AND_CUSTOM_AUTH_FACTORS',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) =>
      form?.includeUserActions === 'urn:user:registerdevice' &&
      (form?.builtInControls?.length > 1 || (form?.builtInControls?.length && !form?.builtInControls?.includes('mfa'))),
    errorMessage: 'POLICY.VALIDATION.USER_ACTIONS_AND_BUILTIN_CONTROLS',
  },
  // grant controls
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) =>
      form?.builtInControls?.includes(BUILT_IN_CONTROLS.BLOCK) &&
      (form?.builtInControls?.length > 1 || form?.authenticationStrength || form?.customAuthenticationFactors?.length),
    errorMessage: 'POLICY.VALIDATION.BLOCK_CONTROLS_CONFLICT',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: hasUnsupportedAppProtectionPlatforms,
    errorMessage: 'POLICY.VALIDATION.APP_PROTECTION_SUPPORTED_PLATFORMS',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: hasUnsupportedApprovedClientAppPlatforms,
    errorMessage: 'POLICY.VALIDATION.APPROVED_CLIENT_APP_SUPPORTED_PLATFORMS',
  },
  {
    input: INPUT_KEYS.authenticationStrength,
    error: (form) => form?.authenticationStrength && form?.builtInControls?.includes(BUILT_IN_CONTROLS.MFA),
    errorMessage: 'POLICY.VALIDATION.AUTH_STRENGTH_MFA_CONFLICT',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) => isPasswordChangeSelected(form) && !hasPasswordChangeUserRisk(form),
    errorMessage: 'POLICY.VALIDATION.PASSWORD_CHANGE_REQUIRE_USER_RISK',
  },
  {
    input: INPUT_KEYS.authenticationStrength,
    error: (form) => isPasswordChangeSelected(form) && !form?.authenticationStrength,
    errorMessage: 'POLICY.VALIDATION.AUTH_STRENGTH_REQUIRED_WITH_PASSWORD_CHANGE',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) => isPasswordChangeSelected(form) && form?.builtInControls?.length > 1,
    errorMessage: 'POLICY.VALIDATION.PASSWORD_CHANGE_BUILTIN_CONFLICT',
  },
  {
    input: INPUT_KEYS.builtInControls,
    error: (form) => !hasPasswordChangeAllApplications(form) && isPasswordChangeSelected(form),
    errorMessage: 'POLICY.VALIDATION.PASSWORD_CHANGE_REQUIRE_ALL_APPS',
  },
  {
    input: INPUT_KEYS.grantControlsOperator,
    error: hasInvalidPasswordChangeOperator,
    errorMessage: 'POLICY.VALIDATION.PASSWORD_CHANGE_REQUIRE_OPERATOR_AND',
  },
  {
    input: INPUT_KEYS.grantControlsOperator,
    error: (form) => hasGrantControls(form) && !form.grantControlsOperator && !isPasswordChangeSelected(form),
    errorMessage: 'POLICY.VALIDATION.OPERATOR_REQUIRED',
  },
]

// helper function to build the error object for the validations
const updateErrorsObject = (errors, input, errorToInsert) => {
  if (errors[input]) {
    errors[input].push(errorToInsert)
  } else {
    errors[input] = [errorToInsert] // eslint-disable-line no-param-reassign
  }
}

export const validatePolicyForm = (form, policyFormOptionsMap, intl, options = {}) => {
  const errors = {}
  const skippedInputs = new Set(options.skippedInputs ?? [])

  // custom validations
  POLICY_FORM_VALIDATIONS.forEach((validation) => {
    if (skippedInputs.has(validation.input)) {
      return
    }

    if (validation.error(form)) {
      const errorMessage = intl.formatMessage({ id: validation.errorMessage })
      updateErrorsObject(errors, validation.input, errorMessage)
    }
  })

  // input value validation - check if input values exist as options
  // since user can add anything when editing policy through json editor
  INPUT_LIST.forEach((input) => {
    if (skippedInputs.has(input) || FORM_KEYS_SKIP_OPTION_VALIDATION.has(input)) {
      return
    }

    const optionMap = policyFormOptionsMap?.[input]
    if (!optionMap) {
      return
    }

    // An empty custom-factor map means we could not see what the tenant uses. That missing catalog
    // must not block editing a policy that already has a factor, or adding one through the JSON editor.
    if (input === INPUT_KEYS.customAuthenticationFactors && Object.keys(optionMap).length === 0) {
      return
    }

    const arrayValues = toArray(form[input])
    arrayValues.forEach((value) => {
      // A group or named location requirement reference is resolved automatically at deploy
      // time, not typed by the technician, so it never appears in policyFormOptionsMap and
      // isn't a mistake to flag.
      if (isAnyRequirementRef(value)) {
        return
      }

      if (!optionMap[value]) {
        const errorMessage = intl.formatMessage({ id: 'POLICY_MANAGEMENT.INVALID_VALUE' }, { value })
        updateErrorsObject(errors, input, errorMessage)
      }
    })
  })

  return errors
}

/**
 * Returns a map of which form inputs Policy Details should show.
 * Grant/general inputs start visible; condition/session inputs start visible only when filled.
 * `skippedInputs` stay hidden even when the policy body has a value for them.
 * `forcedInputs` stay visible even when empty (template accordion prompted fields).
 */
export const getInitialVisibleInputs = (form, options = {}) => {
  const skippedInputs = new Set(options.skippedInputs ?? [])
  const forcedInputs = new Set(options.forcedInputs ?? [])
  const visibleInputsMap = {}

  GRANT_CONTROLS_INPUTS_LIST.concat(GENERAL_INPUTS_LIST).forEach((input) => {
    visibleInputsMap[input] = forcedInputs.has(input) || !skippedInputs.has(input)
  })

  CONDITIONS_INPUTS_LIST.concat(SESSION_CONTROLS_INPUTS_LIST).forEach((input) => {
    visibleInputsMap[input] =
      forcedInputs.has(input) || (!skippedInputs.has(input) && hasDisplayableFormValue(form?.[input]))
  })

  return visibleInputsMap
}

/**
 * Policy schema
 * Note that here we are type checking the policy json itself (which is different from the form schema)
 */
export const policySchema = yup.object().shape({
  // partialEnablementStrategy: null, // @TODO: validate this property?
  displayName: yup.string(),
  grantControls: yup
    .object()
    .nullable()
    .shape({
      authenticationStrength: null,
      termsOfUse: yup.array().of(yup.string()).nullable(),
      customAuthenticationFactors: yup.array().of(yup.string()).nullable(),
      builtInControls: yup.array().of(yup.string()).nullable(),
      operator: yup.string(),
    }),
  state: yup.string(),
  sessionControls: yup
    .object()
    .nullable()
    .shape({
      disableResilienceDefaults: yup.boolean().nullable(),
      signInFrequency: yup.object().nullable().shape({
        isEnabled: yup.boolean().nullable(),
        value: yup.number().nullable(),
        type: yup.string().nullable(),
        frequencyInterval: yup.string().nullable(),
        authenticationType: yup.string().nullable(),
      }),
      persistentBrowser: yup.object().nullable().shape({
        isEnabled: yup.boolean().nullable(),
        mode: yup.string().nullable(),
      }),
      applicationEnforcedRestrictions: yup.object().nullable().shape({
        isEnabled: yup.boolean().nullable(),
      }),
      cloudAppSecurity: yup.object().nullable().shape({
        isEnabled: yup.boolean().nullable(),
        cloudAppSecurityType: yup.string().nullable(),
      }),
      continuousAccessEvaluation: yup.object().nullable().shape({
        mode: yup.string().nullable(),
      }),
      secureSignInSession: yup.object().nullable().shape({
        isEnabled: yup.boolean().nullable(),
      }),
      globalSecureAccessFilteringProfile: yup.object().nullable().shape({
        isEnabled: yup.boolean().nullable(),
        profileId: yup.string().nullable(),
      }),
    }),
  conditions: yup.object().shape({
    clientAppTypes: yup.array().of(yup.string()).nullable(),
    servicePrincipalRiskLevels: yup.array().of(yup.string()).nullable(),
    clientApplications: yup
      .object()
      .nullable()
      .shape({
        includeServicePrincipals: yup.array().of(yup.string()).nullable(),
        excludeServicePrincipals: yup.array().of(yup.string()).nullable(),
        servicePrincipalFilter: yup.object().nullable().shape({
          mode: yup.string().nullable(),
          rule: yup.string().nullable(),
        }),
      }),
    devices: yup
      .object()
      .nullable()
      .shape({
        includeDevices: yup.array().of(yup.string()).nullable(),
        excludeDevices: yup.array().of(yup.string()).nullable(),
        deviceFilter: yup.object().nullable().shape({
          mode: yup.string().nullable(),
          rule: yup.string().nullable(),
        }),
      }),
    locations: yup
      .object()
      .nullable()
      .shape({
        includeLocations: yup.array().of(yup.string()).nullable(),
        excludeLocations: yup.array().of(yup.string()).nullable(),
      }),
    signInRiskLevels: yup.array().of(yup.string()).nullable(),
    userRiskLevels: yup.array().of(yup.string()).nullable(),
    insiderRiskLevels: yup.string().nullable(),
    agentIdRiskLevels: yup.string().nullable(),
    users: yup.object().shape({
      includeRoles: yup.array().of(yup.string()).nullable(),
      excludeGuestsOrExternalUsers: yup
        .object()
        .nullable()
        .shape({
          guestOrExternalUserTypes: yup.string(),
          externalTenants: yup.object().shape({
            membershipKind: yup.string(),
            members: yup.array().of(yup.string()),
          }),
        }),
      excludeGroups: yup.array().of(yup.string()).nullable(),
      excludeRoles: yup.array().of(yup.string()).nullable(),
      excludeUsers: yup.array().of(yup.string()).nullable(),
      includeUsers: yup.array().of(yup.string()).nullable(),
      includeGuestsOrExternalUsers: yup
        .object()
        .nullable()
        .shape({
          guestOrExternalUserTypes: yup.string(),
          externalTenants: yup.object().shape({
            membershipKind: yup.string(),
            members: yup.array().of(yup.string()),
          }),
        }),
      includeGroups: yup.array().of(yup.string()).nullable(),
    }),
    platforms: yup
      .object()
      .nullable()
      .shape({
        includePlatforms: yup.array().of(yup.string()).nullable(),
        excludePlatforms: yup.array().of(yup.string()).nullable(),
      }),
    // deviceStates: null, // @TODO: validate when we start supporting it in the form
    applications: yup.object().shape({
      includeUserActions: yup.array().of(yup.string()).nullable(),
      includeAuthenticationContextClassReferences: yup.array().of(yup.string()).nullable(),
      includeApplications: yup.array().of(yup.string()).nullable(),
      applicationFilter: yup.object().nullable().shape({
        mode: yup.string().nullable(),
        rule: yup.string().nullable(),
      }),
      excludeApplications: yup.array().of(yup.string()).nullable(),
    }),
  }),
})

/**
 * Validates the raw policy (json) schema, returns an array with the validation errors
 * @param {object} rawPolicy
 */
export const validatePolicySchema = (rawPolicy) => {
  let errors = []
  try {
    policySchema.validateSync(rawPolicy, { abortEarly: false, strict: true })
  } catch (err) {
    errors = err.errors
  }
  return errors
}

/**
 * Validate fields that could be in the json editor and are not in the form (so are not validated by the form validation)
 * Differs from the `validatePolicySchema` which is schema specific
 */
export const validateEditorPayload = (rawPolicy, intl) => {
  const errors = []

  const nonEditableFields = ['id', 'createdDateTime', 'modifiedDateTime']
  nonEditableFields.forEach((field) => {
    if (rawPolicy?.[field]) {
      errors.push(intl.formatMessage({ id: 'POLICY_TEMPLATES.FIELD_NOT_EDITABLE' }, { field }))
    }
  })

  return errors
}
